import { useEffect, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { ConnectedWorkbench } from './ConnectedWorkbench';

import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth/AuthContext';
import { usePermissions } from '@/features/permissions';

export function LoginBriefing({ initiallyOpen = false }: { initiallyOpen?: boolean }) {
  const { user, organization } = useAuth();
  const { can } = usePermissions();
  if (!user || !organization || !can('use_assistant') || !can('review_assistant_actions')) return null;
  return (
    <Briefing
      key={`${organization.organization_id}:${user.id}`}
      org={organization.organization_id}
      initiallyOpen={initiallyOpen}
    />
  );
}

function Briefing({ org, initiallyOpen }: { org: string; initiallyOpen: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const [enabled, setEnabled] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    const abort = new AbortController();
    const base = `/api/v1/organizations/${org}/assistant`;
    let refreshing = false;
    const refresh = async () => {
      if (refreshing || abort.signal.aborted) return;
      refreshing = true;
      try {
        const { data } = await clientApi.get<{ workbench_enabled?: boolean }>(`${base}/capabilities`, {
          signal: abort.signal,
        });
        if (abort.signal.aborted) return;
        setEnabled(Boolean(data.workbench_enabled));
        if (!data.workbench_enabled) {
          setCount(null);
          return;
        }
        try {
          const result = await clientApi.get<{ new_count: number }>(`${base}/workbench/briefing`, {
            signal: abort.signal,
          });
          if (!abort.signal.aborted) setCount(result.data.new_count);
        } catch {
          /* Settings remain accessible before first configuration. */
          if (!abort.signal.aborted) setCount(null);
        }
      } catch {
        if (!abort.signal.aborted) {
          setEnabled(false);
          setCount(null);
        }
      } finally {
        refreshing = false;
      }
    };
    const refreshVisible = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    void refresh();
    const timer = window.setInterval(refreshVisible, 10000);
    document.addEventListener('visibilitychange', refreshVisible);
    return () => {
      abort.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshVisible);
    };
  }, [org]);
  if (!enabled) return null;
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button size="sm" type="button" variant="outline">
          <Sparkles aria-hidden="true" />
          Assistant{count !== null && count > 0 ? ` · ${count} new` : ''}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>Assistant</SheetTitle>
          <SheetDescription>Decisions and follow-ups, one item at a time.</SheetDescription>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">{open && <ConnectedWorkbench />}</div>
      </SheetContent>
    </Sheet>
  );
}
